import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import './umsetzung.css'

/**
 * Umsetzung — neun Folien, waagerecht, eine je Bildschirm.
 *
 * AUFBAU UND INHALT SIND VORGEGEBEN und stehen hier unverändert: Titel,
 * Leitsätze, Zielbild, dann sechs nummerierte Kapitel von der Struktur bis zu
 * den Kosten. Was angepasst wurde, ist ausschliesslich die Erscheinung — die
 * Seite lag als eigenständige HTML-Datei mit eigenen Farben und einer eigenen
 * Schrift vor; hier trägt sie die Tokens dieser Anwendung und wechselt das
 * Register mit ihr.
 *
 * WO FARBE BLEIBT UND WO SIE GEHT. Die Marke ist monochrom mit einer kalten
 * Lichtfarbe an Kanten, und die drei durchgefärbten Strukturkacheln der
 * Vorlage sind deshalb einfarbig geworden: dort unterschied die Farbe nichts,
 * was nicht schon in der Überschrift steht. Die Punkte im Werkzeugkasten und
 * an den Meilensteinen bleiben farbig — dort IST die Farbe die Aussage. Ohne
 * sie stünde in beiden Listen nur noch, dass es etwas gibt, und nicht mehr,
 * was daran gesetzt, vorgeschlagen oder offen ist.
 *
 * DIE ZAHLEN STEHEN ALS TEXT, und das ist hier richtig. Sie sind Festlegungen
 * aus der Vorlage und kein Rechenergebnis: Fläche, Besetzung, Budget. Wer sie
 * an einen Regler hängte, behauptete eine Herleitung, die es nicht gibt.
 */

/* --- Inhalt der Folien ------------------------------------------------------ */

const CREED: readonly { title: string; text: string }[] = [
  {
    title: 'Das Produkt',
    text: 'Das Produkt allein steht im Zentrum. Es geht immer um das Produkt.',
  },
  {
    title: 'Der Anspruch',
    text: 'Das Ergebnis wird nicht abgespeckt. Wir kürzen den Weg, nicht das Ziel.',
  },
  { title: 'Der Termin', text: 'Wir müssen ein Rennen gewinnen. Wir schieben nichts auf.' },
  { title: 'Die Qualität', text: 'Wir gehen keine Kompromisse ein. Qualität von Beginn an.' },
]

const GOAL: readonly { title: string; text: string }[] = [
  { title: 'Performance', text: 'Kennwerte, die sich mit den Besten vergleichen lassen.' },
  { title: 'Auftreten', text: 'Natürliche und runde Bewegungen.' },
  { title: 'Alleinstellung', text: 'Der Roboter kann etwas, was sonst keiner kann.' },
]

const OPTIONS: readonly { title: string; owner: string; merge: string }[] = [
  { title: 'Ohne weitere Gesellschaft', owner: 'NEURA', merge: 'entfällt' },
  { title: 'Tochtergesellschaft von NEURA', owner: 'NEURA', merge: 'entfällt, ist Eigentum' },
  { title: 'Eigenständige Gesellschaft im Auftrag', owner: 'unabhängig', merge: 'nach Absprache' },
]

const RAMP: readonly { when: string; count: string }[] = [
  { when: 'September', count: '5' },
  { when: 'Ausbau', count: 'bis zu 12' },
]

const PARTNERS: readonly { name: string; note?: string }[] = [
  { name: 'aiDUS' },
  { name: 'Renco Köln' },
  { name: 'Compact Dynamics' },
  { name: 'Toyota Gazoo Racing' },
  { name: 'Becker Engineering' },
  { name: 'HS Düsseldorf', note: 'in Klärung' },
  { name: 'AI Village Köln', note: 'in Klärung' },
]

/** Stand eines Werkzeugs. Die Farbe des Punktes hängt daran. */
type Stand = 'set' | 'vor' | 'off'

/**
 * Der Werkzeugkasten.
 *
 * KIRON STEHT HIER ALS FREMDES WERKZEUG UND SONST NIRGENDS — ausdrücklich
 * freigegeben („im werkzeugkasten kann es stehen", 09/2026).
 *
 * Die Trennung in `CLAUDE.md` verlangt für einen Kiron-Begriff in dieser
 * Oberfläche genau das: eine Freigabe. Sie liegt vor, und weiter reicht sie
 * nicht. Was verboten bleibt, bleibt verboten: gemeinsamer Code, gemeinsame
 * Datenbank, gemeinsames Schema, ein Kiron-Schlüssel in diesem Repository.
 * Hier steht ein Name in einer Aufstellung der benutzten Programme. Kein
 * Zugang, keine Zeile Code.
 */
const TOOLBOX: readonly {
  title: string
  items: readonly { stand: Stand; name: string; note?: string }[]
}[] = [
  {
    title: 'Wahrheit',
    items: [
      {
        stand: 'set',
        name: 'Master Manager',
        note: 'Eigenbau · Kinematik, Massen, Leistungen · MuJoCo integriert',
      },
      { stand: 'set', name: 'Kiron', note: 'PLM und ERP · Stückliste, Teilestatus, Beschaffung' },
      { stand: 'set', name: 'Datenversionierung', note: 'über PLM und ERP' },
    ],
  },
  {
    title: 'Konstruktion',
    items: [
      { stand: 'set', name: 'Siemens NX', note: 'CAD' },
      { stand: 'set', name: 'Siemens NX CAE', note: 'Struktur, Sturzlastfall, Thermik' },
      { stand: 'off', name: 'Motorauslegung', note: 'to be defined' },
    ],
  },
  {
    title: 'Elektronik',
    items: [
      { stand: 'set', name: 'KiCad', note: 'Schaltplan und Leiterplatte' },
      { stand: 'vor', name: 'PLECS', note: 'Leistungselektronik und Regelkreis' },
      { stand: 'vor', name: 'LTspice', note: 'Bauteilebene' },
      { stand: 'vor', name: 'Zephyr', note: 'Firmware am Gelenk' },
      { stand: 'vor', name: 'EtherCAT über SOEM', note: 'Bus zwischen Gelenk und Rechner' },
    ],
  },
  {
    title: 'Simulation und Lernen',
    items: [
      { stand: 'set', name: 'NVIDIA GR00T N1.7', note: 'Vision-Language-Action-Modell' },
      { stand: 'set', name: 'Isaac Lab und Isaac Sim', note: 'Training und Stresstest' },
      {
        stand: 'set',
        name: 'MuJoCo',
        note: 'im Master Manager integriert · zweite Physik zur Gegenprobe',
      },
      { stand: 'vor', name: 'Motion Retargeting', note: 'menschliche Aufnahmen auf den Roboter' },
      { stand: 'set', name: 'Experiment-Tracking', note: 'Eigenbau' },
      { stand: 'set', name: 'GPU-Leistung', note: 'gemietet' },
    ],
  },
  {
    title: 'Maschine',
    items: [
      { stand: 'vor', name: 'ROS 2', note: 'Middleware' },
      { stand: 'vor', name: 'ros2_control', note: 'Echtzeitregelung am Gelenk' },
      { stand: 'vor', name: 'Foxglove mit MCAP', note: 'Logging und Auswertung' },
    ],
  },
  {
    title: 'KI-Assistenz',
    items: [{ stand: 'set', name: 'Claude', note: 'Auslegung, Recherche, Dokumentation, Code' }],
  },
]

const TOOLBOX_LEGEND: readonly { stand: Stand; label: string }[] = [
  { stand: 'set', label: 'gesetzt' },
  { stand: 'vor', label: 'Vorschlag' },
  { stand: 'off', label: 'offen' },
]

/** Gewerk eines Meilensteins. Auch hier trägt die Farbe die Aussage. */
type Gewerk = 'hw' | 'in' | 'sw'

const GATES: readonly {
  date: string
  items: readonly { gewerk: Gewerk; label: string; note?: string }[]
}[] = [
  { date: '05.09.2026', items: [{ gewerk: 'in', label: 'Kick off' }] },
  { date: '15.09.2026', items: [{ gewerk: 'in', label: 'Handlungsfähig' }] },
  { date: '30.09.2026', items: [{ gewerk: 'hw', label: 'Architektur verifiziert' }] },
  {
    date: '15.10.2026',
    items: [
      { gewerk: 'hw', label: 'Kinematikmodell Hardware', note: '3D-Druck-Puppe ohne Motoren' },
      { gewerk: 'in', label: 'Motionlab bereit' },
      { gewerk: 'sw', label: 'USP definiert' },
    ],
  },
  { date: '30.10.2026', items: [{ gewerk: 'hw', label: 'Motoren verifiziert und getestet' }] },
  {
    date: '30.12.2026',
    items: [
      { gewerk: 'hw', label: 'Alle Teile P00 in Produktion' },
      { gewerk: 'sw', label: 'Szenen und Trainingsumfang definiert' },
    ],
  },
  {
    date: '30.01.2027',
    items: [
      { gewerk: 'hw', label: 'Alle Teile im Haus' },
      { gewerk: 'hw', label: 'Platine bereit' },
    ],
  },
  { date: '20.02.2027', items: [{ gewerk: 'hw', label: 'Hardware assembled' }] },
  {
    date: '28.02.2027',
    items: [
      { gewerk: 'hw', label: 'Erste Inbetriebnahme P00', note: 'Hardware und Motoren' },
      { gewerk: 'sw', label: 'Wahrnehmung bereit' },
    ],
  },
  {
    date: '30.03.2027',
    items: [
      { gewerk: 'hw', label: 'P01 Hardware vorhanden' },
      { gewerk: 'sw', label: 'Erste eigene Interaktionen' },
    ],
  },
  {
    date: '30.04.2027',
    items: [
      { gewerk: 'hw', label: 'Hardware P02 bereit' },
      { gewerk: 'sw', label: 'P01 bereit' },
    ],
  },
  {
    date: '30.05.2027',
    items: [
      { gewerk: 'sw', label: 'Feinabstimmung' },
      { gewerk: 'in', label: 'Vorbereitung Automatica' },
    ],
  },
]

const GATE_LEGEND: readonly { gewerk: Gewerk; label: string }[] = [
  { gewerk: 'hw', label: 'Hardware' },
  { gewerk: 'in', label: 'Infrastruktur' },
  { gewerk: 'sw', label: 'Software' },
]

const SITE: readonly { title: string; text: string }[] = [
  { title: 'Zustand', text: 'ausgebaut übernehmen, kein Mieterausbau' },
  { title: 'Laufzeit', text: 'ein Jahr, Option auf Verlängerung' },
]

/*
  DIE OBJEKTÜBERSICHT LIEGT ALS DATEI DANEBEN UND NICHT IM BÜNDEL.

  Sechs Megabyte PDF gehören nicht in den JavaScript-Stand: dort würden sie
  bei jedem Aufruf der Anwendung mitgeladen, auch von den neunzig Prozent, die
  nie auf diese Folie kommen. Als Datei unter `public/` holt der Browser sie
  erst beim Klick — und der Pfad bleibt derselbe, wenn die Anwendung eines
  Tages unter einem Unterverzeichnis läuft.
*/
const SITE_PDF = `${import.meta.env.BASE_URL}dokumente/standortoptionen-duesseldorf.pdf`

const COST: readonly { label: string; value: string; accent?: boolean }[] = [
  { label: 'Personal', value: '1.200.000 €' },
  { label: 'Einmalkosten initial', value: '80.000 €' },
  { label: 'Standort', value: '100.000 €' },
  { label: 'Externe', value: '500.000 €' },
  { label: 'Sonstiges', value: '250.000 €' },
  { label: 'Summe', value: '2.130.000 €' },
  { label: 'Reserve', value: '870.000 €' },
  { label: 'Wunschbudget', value: '3.000.000 €', accent: true },
]

/* --- Bausteine -------------------------------------------------------------- */

/** Eine Kennzahlkachel — dieselbe Form wie überall im Werkzeug (`.metric`). */
function Fig({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`metric${accent ? ' metric--accent' : ''}`}>
      <span className="metric__label">{label}</span>
      <span className="metric__value">{value}</span>
    </div>
  )
}

/** Der nummerierte Satzblock — Leitsätze und Zielbild tragen dieselbe Form. */
function Creed({ rules }: { rules: readonly { title: string; text: string }[] }) {
  return (
    <section className="creed">
      <ol className="guidance">
        {rules.map((rule) => (
          <li key={rule.title} className="guidance__rule">
            <b className="guidance__title">{rule.title}</b>
            <span className="guidance__text">{rule.text}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}

/** Die Farbmarke vor einem Listeneintrag — sie trägt die Einordnung. */
function Mark({ kind }: { kind: Stand | Gewerk }) {
  return <i className={`deck__mark deck__mark--${kind}`} aria-hidden="true" />
}

/* --- Die Seite --------------------------------------------------------------- */

export function UmsetzungPage() {
  const slides: readonly { key: string; body: ReactNode }[] = [
    {
      key: 'titel',
      body: (
        <>
          <p className="deck__eyebrow">Vorhaben · vertraulich</p>
          <h1 className="deck__title">Umsetzung</h1>
          <div className="metrics deck__figs">
            <Fig label="Start" value="01.09.2026" />
            <Fig label="Nachweis" value="31.05.2027" />
            <Fig label="Mitarbeiter" value="12" />
            <Fig label="Prototypen" value="3" />
            <Fig label="Fläche" value="300 m²" />
          </div>
        </>
      ),
    },
    {
      key: 'leitsaetze',
      body: (
        <>
          <p className="deck__eyebrow">01</p>
          <h2 className="deck__head">
            <b>Leitsätze</b>
          </h2>
          <Creed rules={CREED} />
        </>
      ),
    },
    {
      key: 'zielbild',
      body: (
        <>
          <p className="deck__eyebrow">02</p>
          <h2 className="deck__head">
            <b>Zielbild</b>
          </h2>
          <Creed rules={GOAL} />
        </>
      ),
    },
    {
      key: 'struktur',
      body: (
        <>
          <p className="deck__eyebrow">03</p>
          <h2 className="deck__head">
            <b>Struktur</b>
          </h2>
          <div className="deck__opts">
            {OPTIONS.map((opt) => (
              <div key={opt.title} className="deck__opt">
                <h3>{opt.title}</h3>
                <dl>
                  <div>
                    <dt>Eigentum</dt>
                    <dd>{opt.owner}</dd>
                  </div>
                  <div>
                    <dt>Eingliederung</dt>
                    <dd>{opt.merge}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      key: 'team',
      body: (
        <>
          <p className="deck__eyebrow">04</p>
          <h2 className="deck__head">
            <b>Team</b>
          </h2>
          <div className="deck__steps deck__steps--pair">
            {RAMP.map((step) => (
              <div key={step.when} className="deck__step">
                <div className="deck__step-when">{step.when}</div>
                <div className="deck__step-count">{step.count}</div>
              </div>
            ))}
          </div>
          <p className="deck__group">Externe Unterstützung</p>
          <div className="deck__ext">
            {PARTNERS.map((partner) => (
              <div
                key={partner.name}
                className={`deck__step${partner.note ? ' deck__step--open' : ''}`}
              >
                <div className="deck__step-name">{partner.name}</div>
                {partner.note && <div className="deck__step-when">{partner.note}</div>}
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      key: 'werkzeugkasten',
      body: (
        <>
          <p className="deck__eyebrow">05</p>
          <h2 className="deck__head">
            <b>Werkzeugkasten</b>
          </h2>
          <div className="deck__cards deck__cards--wide">
            {TOOLBOX.map((group) => (
              <div key={group.title} className="deck__card">
                <span className="deck__card-title">{group.title}</span>
                <ul>
                  {group.items.map((item) => (
                    <li key={item.name}>
                      <Mark kind={item.stand} />
                      {item.name}
                      {item.note && <span className="deck__card-note">{item.note}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="deck__legend">
            {TOOLBOX_LEGEND.map((entry) => (
              <span key={entry.stand}>
                <Mark kind={entry.stand} />
                {entry.label}
              </span>
            ))}
          </p>
        </>
      ),
    },
    {
      key: 'meilensteine',
      body: (
        <>
          <p className="deck__eyebrow">06</p>
          <h2 className="deck__head">
            <b>Meilensteine</b>
          </h2>
          <div className="deck__cards">
            {GATES.map((gate) => (
              <div key={gate.date} className="deck__card">
                <span className="deck__card-date">{gate.date}</span>
                <ul>
                  {gate.items.map((item) => (
                    <li key={item.label}>
                      <Mark kind={item.gewerk} />
                      {item.label}
                      {item.note && <span className="deck__card-note">{item.note}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="deck__legend">
            {GATE_LEGEND.map((entry) => (
              <span key={entry.gewerk}>
                <Mark kind={entry.gewerk} />
                {entry.label}
              </span>
            ))}
          </p>
          <p className="deck__sub">Dexterhand noch zu klären.</p>
        </>
      ),
    },
    {
      key: 'infrastruktur',
      body: (
        <>
          <p className="deck__eyebrow">07</p>
          <h2 className="deck__head">
            <b>Infrastruktur</b>
          </h2>
          <div className="metrics deck__figs deck__figs--tight">
            <Fig label="Fläche" value="300 m²" />
            <Fig label="davon Werkfläche" value="60 m²" />
            <Fig label="davon Motionlab" value="80 m²" />
            <Fig label="Einzug" value="Sep 2026" />
          </div>
          <ul className="deck__keys">
            {SITE.map((key) => (
              <li key={key.title}>
                <b>{key.title}</b>
                <span>{key.text}</span>
              </li>
            ))}
          </ul>
          {/*
            EIN VERWEIS UND KEINE SCHALTFLÄCHE MIT `window.open`.

            Er sieht aus wie ein Knopf und ist einer — aber als Verweis
            funktioniert er auch mit der mittleren Maustaste, im Kontextmenü
            und für einen Vorleser, und kein Blocker hält ihn für ein
            aufspringendes Fenster.
          */}
          <p className="deck__doc">
            <a className="deck__doc-link" href={SITE_PDF} target="_blank" rel="noopener noreferrer">
              Standortoptionen öffnen
              <span className="deck__doc-hint">PDF · Objektübersicht Düsseldorf · 16 Seiten</span>
            </a>
          </p>
        </>
      ),
    },
    {
      key: 'kosten',
      body: (
        <>
          <p className="deck__eyebrow">08</p>
          <h2 className="deck__head">
            <b>Kosten</b> — neun Monate bis Ende Mai 2027
          </h2>
          <div className="metrics deck__figs deck__figs--tight">
            {COST.map((row) => (
              <Fig key={row.label} label={row.label} value={row.value} accent={row.accent} />
            ))}
          </div>
        </>
      ),
    },
  ]

  const deck = useRef<HTMLDivElement>(null)
  const [current, setCurrent] = useState(0)

  /*
    GEBLÄTTERT WIRD OHNE ÜBERBLENDUNG — und das ist eine Feststellung aus dem
    Browser, keine Vorliebe.

    In einem Behälter mit `scroll-snap-type: mandatory` kommt keine der beiden
    weichen Bewegungen zuverlässig an. Gemessen, jeweils über das ganze Deck:

      – `scroll-behavior: smooth` bzw. `scrollTo({behavior:'smooth'})`: von
        sieben Klicks kamen vier an, ein Klick auf den sechsten Punkt bewegte
        gar nichts. Der Zähler zeigte trotzdem die Folie, die niemand sah.
      – eine eigene Bewegung Bild für Bild: dieselbe Sorte Aussetzer. Der
        Schnappmechanismus zieht zwischen den Bildern zurück.

    Ein Sprung dagegen sitzt jedes Mal. Eine Vorführung, bei der jeder zweite
    Klick nichts tut, ist schlimmer als eine ohne Überblendung — und das
    Schnappen bleibt, damit Wischen und Trackpad weiter Folie für Folie
    einrasten.

    DER STAND WIRD NICHT HIER GESETZT, sondern aus der Bildlaufstelle gelesen.
    Sonst zeigte der Zähler eine Folie, die aus irgendeinem Grund nie erreicht
    wurde — genau der Fehler oben.
  */
  const go = useCallback(
    (index: number) => {
      const node = deck.current
      if (!node) return
      const next = Math.max(0, Math.min(slides.length - 1, index))
      node.scrollLeft = next * node.clientWidth
    },
    [slides.length],
  )

  useEffect(() => {
    const node = deck.current
    if (!node) return
    let timer: number | undefined

    const settle = () => {
      const index = Math.round(node.scrollLeft / node.clientWidth)
      setCurrent((was) => (was === index ? was : index))
    }
    /* `scrollend` ist das genaue Signal; der abgebremste Bildlauf ist der
       Rückfall für Browser, die es nicht kennen. Beide melden dasselbe. */
    const onScroll = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(settle, 120)
    }

    node.addEventListener('scrollend', settle)
    node.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.clearTimeout(timer)
      node.removeEventListener('scrollend', settle)
      node.removeEventListener('scroll', onScroll)
    }
  }, [])

  /*
    DIE PFEILTASTEN BLÄTTERN. Ein Deck, das nur mit der Maus zu bedienen ist,
    wird in einer Vorführung zum Hindernis — dort steht jemand vor dem Schirm
    und hat eine Fernbedienung, die genau diese Tasten sendet.
  */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      /* Nicht, während jemand tippt — die Suche in der Kopfzeile braucht ihre
         Pfeiltasten selbst. */
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable]')) return
      if (event.metaKey || event.ctrlKey || event.altKey) return

      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault()
        go(current + 1)
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault()
        go(current - 1)
      } else if (event.key === 'Home') {
        go(0)
      } else if (event.key === 'End') {
        go(slides.length - 1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [current, go, slides.length])

  /* Beim Wechsel der Fensterbreite steht die Folie sonst halb im Bild. */
  useEffect(() => {
    const onResize = () => {
      const node = deck.current
      if (node) node.scrollLeft = current * node.clientWidth
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [current])

  const pad = (value: number) => String(value).padStart(2, '0')

  return (
    <div>
      <div className="deck" ref={deck}>
        {slides.map((slide) => (
          <section key={slide.key} className="deck__slide">
            <div className="deck__inner">{slide.body}</div>
          </section>
        ))}
      </div>

      <div className="deck-bar">
        <div className="deck-bar__dots">
          {slides.map((slide, index) => (
            <button
              key={slide.key}
              type="button"
              className="deck-bar__dot"
              aria-current={index === current}
              aria-label={`Folie ${index + 1}`}
              onClick={() => go(index)}
            />
          ))}
        </div>
        <span className="deck-bar__count">
          {pad(current + 1)} / {pad(slides.length)}
        </span>
        <button
          type="button"
          className="deck-bar__arrow"
          aria-label="zurück"
          disabled={current === 0}
          onClick={() => go(current - 1)}
        >
          ←
        </button>
        <button
          type="button"
          className="deck-bar__arrow"
          aria-label="weiter"
          disabled={current === slides.length - 1}
          onClick={() => go(current + 1)}
        >
          →
        </button>
      </div>
    </div>
  )
}
